package com.htmlagent.shared.session

import com.htmlagent.shared.UiLocale
import kotlinx.serialization.Serializable

/** The created session, with the public identifier of its artifact. */
@Serializable
data class StartSessionResponse(
    val sessionId: String,
    /**
     * Public identifier used to build the preview URL. Different from [sessionId]: that URL
     * goes to another origin and cannot carry the conversation's credential.
     */
    val artifactId: String,
)

/**
 * The message the user writes in the chat.
 *
 * [locale] is the language of the interface, and it is what the agent answers and writes the
 * page in. It has a default so an older client keeps working; the default is Spanish because
 * that is what every session created before this field existed was using.
 *
 * [browserLocale] and [timeZone] are what the browser reports (`es-CO`, `America/Bogota`): the
 * country the invented facts of a page — phones, prices, addresses — should belong to.
 */
@Serializable
data class PostMessageRequest(
    val prompt: String,
    val locale: UiLocale = UiLocale.SPANISH,
    val browserLocale: String? = null,
    val timeZone: String? = null,
    /** Photos of sites the person likes, as visual reference. At most four. */
    val images: List<ImageUpload> = emptyList(),
)

/**
 * A reference photo as it travels in a message: base64 inside the JSON, already shrunk by the
 * browser. Base64 and not multipart so the message and its photos are one request that either
 * arrives whole or not at all.
 */
@Serializable
data class ImageUpload(
    /** `image/jpeg`, `image/png` or `image/webp`. */
    val mediaType: String,
    val base64: String,
)

/**
 * The new text of one of the person's messages. Editing rewinds the conversation to it and sends
 * the new text as a fresh turn, so it carries what [PostMessageRequest] carries.
 */
@Serializable
data class EditMessageRequest(
    val text: String,
    val locale: UiLocale = UiLocale.SPANISH,
    val browserLocale: String? = null,
    val timeZone: String? = null,
    /**
     * The positions of the message's photos to keep, in order. `null` keeps them all, which is
     * what a client that knows nothing of photos means.
     */
    val keptImages: List<Int>? = null,
)

/**
 * A history version without its HTML, which can weigh hundreds of kilobytes and is fetched
 * separately by version number. [label] is the number shown: `3`, or `3.1` for a fix on it.
 */
@Serializable
data class ArtifactVersionSummary(
    val version: Int,
    val label: String,
    val createdAt: String,
    val sizeInBytes: Int,
)

/** The version history of a session. */
@Serializable
data class ArtifactHistoryResponse(
    /** Repeated here so the frontend can rebuild the URLs after a reload. */
    val artifactId: String,
    val versions: List<ArtifactVersionSummary>,
)

/**
 * A session in the user's list. `lastActiveAt` travels as ISO-8601 text and not as a
 * number, so it does not rely on a tacit agreement about the unit.
 */
@Serializable
data class SessionSummaryResponse(
    val sessionId: String,
    val artifactId: String,
    /** The first request, trimmed. Null while the session is still empty. */
    val title: String?,
    val lastActiveAt: String,
    val versionCount: Int,
)

/** A user's sessions. */
@Serializable
data class SessionListResponse(val sessions: List<SessionSummaryResponse>)

/** A history turn, so a conversation can be rebuilt when the user comes back to it. */
@Serializable
data class HistoryMessageResponse(
    /** What editing or deleting it is addressed by. */
    val id: Long,
    /** `user` or `assistant`. Tool messages do not come out: they are internal noise. */
    val role: String,
    val text: String,
    /** How many reference photos it carries; each one is fetched by its position. */
    val imageCount: Int = 0,
)

/**
 * A reopened session, with its conversation. Without the HTML, which the preview loads from
 * the artifact origin, and without the versions, which have their own endpoint.
 */
@Serializable
data class SessionDetailResponse(
    val sessionId: String,
    val artifactId: String,
    val messages: List<HistoryMessageResponse>,
    /**
     * What it has consumed so far. It comes with the session and not from an endpoint of its
     * own: the header paints it on opening, and a second round trip for one figure is a
     * request nobody needs to make.
     */
    val usage: SessionUsageResponse,
)

/**
 * What a session has consumed, and what that is worth.
 *
 * Tokens and money travel together on purpose: the amount alone hides where it came from, and a
 * turn that overspends is always overspending on one of the two halves.
 *
 * [costMicros] is in millionths of [currency] and not a decimal, because money in a `Double`
 * drifts once it is added up. The interface divides once, to paint it.
 *
 * It is an ESTIMATE: the price comes from configuration, and where the provider serves part of
 * the prompt from its cache the real bill is lower.
 */
@Serializable
data class SessionUsageResponse(
    val turns: Int,
    val inputTokens: Int,
    val outputTokens: Int,
    val costMicros: Long,
    /** ISO-4217, so the interface can put a symbol next to the figure. */
    val currency: String,
) {
    val totalTokens: Int get() = inputTokens + outputTokens
}
